import { useEffect, useRef, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { agentPresenceEnabled, getPresence } from '../../../lib/agent-presence-api';
import { findAnswer, type IPresenceAnswer, seedFromStored, seedOwnParty, toPresenceRead } from './agent-presence';

export type PresenceReadStatus = 'idle' | 'loading' | 'loaded' | 'none' | 'unavailable';

/** Whether polling-agent capture applies here: polling units only, and only behind the flag. */
export const usePresenceEnabled = (): boolean => {
	const {
		global: {
			state: { resultType },
		},
	} = useGlobalContext();
	return resultType === 'EC8A' && agentPresenceEnabled();
};

/**
 * Seeds the form when Party Vote Count opens:
 *  1. the agent's own party → `present`, with their name (they are that party's agent);
 *  2. whatever the server already holds, for parties this agent has not touched.
 *
 * Both only ever FILL blanks — a restored draft is never clobbered. The read is a courtesy
 * (so a re-opened unit shows its answers); the save re-reads before writing regardless, so a
 * failed read here costs a hint on screen and nothing else.
 */
export const useAgentPresenceSeed = (rosterIds: number[]): { readStatus: PresenceReadStatus } => {
	const {
		global: {
			state: { agentPresence, selectedParentCode, electionData },
			updateState,
		},
	} = useGlobalContext();
	const enabled = usePresenceEnabled();
	const [readStatus, setReadStatus] = useState<PresenceReadStatus>('idle');

	// The GET resolves after renders that may have changed the answers; merge into the latest.
	const answersRef = useRef<IPresenceAnswer[]>(agentPresence);
	answersRef.current = agentPresence;

	const electionId = Number(electionData?.data?.[0]?.electionId) || 0;
	const code = selectedParentCode?.code || '';
	const rosterKey = rosterIds.join(',');

	// 1 — own party. Keyed on the roster so it lands once the party list arrives.
	useEffect(() => {
		if (!enabled || !rosterIds.length) return;
		const { user } = getUserData();
		const ownPartyId = Number(user?.PartyId) || 0;
		if (!ownPartyId || findAnswer(answersRef.current, ownPartyId)) return;
		const next = seedOwnParty(answersRef.current, ownPartyId, user?.FullName || '', rosterIds);
		if (next.length !== answersRef.current.length) updateState('agentPresence', next);
	}, [enabled, rosterKey]);

	// 2 — the stored record, once per unit.
	useEffect(() => {
		if (!enabled || !electionId || !code) return;
		let cancelled = false;
		setReadStatus('loading');
		getPresence(electionId, code).then(({ status, body }) => {
			if (cancelled) return;
			const read = toPresenceRead(status, body);
			if (read.kind === 'error') {
				setReadStatus('unavailable');
				return;
			}
			if (read.kind === 'none' || !read.parties.length) {
				setReadStatus('none');
				return;
			}
			const next = seedFromStored(answersRef.current, read.parties);
			if (next.length !== answersRef.current.length) updateState('agentPresence', next);
			setReadStatus('loaded');
		});
		return () => {
			cancelled = true;
		};
	}, [enabled, electionId, code]);

	return { readStatus };
};
